/**
 * What the crag routes page is looking at travels in the query string:
 * `?sector=4&route=91`. A sector half worked through can be pasted to whoever
 * is bolting it, and a reload comes back to it rather than to the first cliff.
 *
 * Query string rather than path params, for the same reason the scans page uses
 * them: a path param means a second `<Route>`, and matching a different route
 * remounts the page — which would throw away the line being drawn and the
 * form fields typed but not yet saved.
 */

const SECTOR = 'sector';
const ROUTE = 'route';
const VIEW = 'view';

/** What the routes are drawn on. A photo unless the URL says otherwise. */
export type CragRouteView = 'photo' | 'model';

export interface CragRouteSelection {
  sectorId: string | null;
  routeId: string | null;
  view: CragRouteView;
}

export function readCragRouteSelection(params: URLSearchParams): CragRouteSelection {
  return {
    sectorId: params.get(SECTOR)?.trim() || null,
    routeId: params.get(ROUTE)?.trim() || null,
    view: params.get(VIEW) === 'model' ? 'model' : 'photo',
  };
}

/**
 * Open a sector. The route goes with it: the one that was open belongs to the
 * cliff being left, and its line was drawn on that cliff's photo.
 */
export function sectorToParams(sectorId: string, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  next.set(SECTOR, sectorId);
  next.delete(ROUTE);

  return next;
}

/** Draw on the photo or on the 3D model. The sector and the route stay open. */
export function viewToParams(view: CragRouteView, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  if (view === 'model') next.set(VIEW, 'model');
  else next.delete(VIEW);

  return next;
}

/** Open one of the sector's routes, or close the one that is open. */
export function routeToParams(routeId: string | null, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  if (routeId) next.set(ROUTE, routeId);
  else next.delete(ROUTE);

  return next;
}
